import { useState } from 'react';

interface Question {
  id: number;
  question: string;
  options: {
    text: string;
    score: number;
  }[];
}

const questions: Question[] = [
  {
    id: 1,
    question: "How does your organization currently approach AI ethics and governance?",
    options: [
      { text: "We have a comprehensive AI ethics framework with dedicated oversight", score: 4 },
      { text: "We have some guidelines but they're not consistently applied", score: 2 },
      { text: "We rely on general tech policies, nothing AI-specific", score: 1 },
      { text: "We haven't addressed this yet", score: 0 }
    ]
  },
  {
    id: 2,
    question: "When AI systems make decisions affecting people, how transparent is your process?",
    options: [
      { text: "Fully transparent with clear explanations for all stakeholders", score: 4 },
      { text: "Transparent internally, limited external communication", score: 2 },
      { text: "Transparency is limited to technical teams only", score: 1 },
      { text: "Our AI decision-making processes are largely opaque", score: 0 }
    ]
  },
  {
    id: 3,
    question: "How do you balance AI efficiency with human oversight and accountability?",
    options: [
      { text: "We have clear protocols ensuring meaningful human oversight", score: 4 },
      { text: "Humans can intervene, but it's not systematically designed", score: 2 },
      { text: "We mostly trust the AI systems to operate autonomously", score: 1 },
      { text: "We haven't established oversight mechanisms yet", score: 0 }
    ]
  },
  {
    id: 4,
    question: "Does your organization actively test for bias and fairness in AI systems?",
    options: [
      { text: "Yes, with regular audits and diverse testing protocols", score: 4 },
      { text: "We test occasionally when issues arise", score: 2 },
      { text: "We rely on vendor assurances about fairness", score: 1 },
      { text: "This isn't part of our current process", score: 0 }
    ]
  },
  {
    id: 5,
    question: "How prepared is your leadership team to navigate AI ethics challenges?",
    options: [
      { text: "Highly prepared with ongoing education and clear policies", score: 4 },
      { text: "Somewhat prepared but knowledge gaps exist", score: 2 },
      { text: "Limited understanding beyond basic AI concepts", score: 1 },
      { text: "This is new territory for our leadership", score: 0 }
    ]
  },
  {
    id: 6,
    question: "When AI and human values conflict, how does your organization respond?",
    options: [
      { text: "We have established frameworks for resolving these tensions", score: 4 },
      { text: "We address conflicts case-by-case as they emerge", score: 2 },
      { text: "We haven't encountered this situation yet", score: 1 },
      { text: "We're not sure how we'd handle this", score: 0 }
    ]
  }
];

const getScoreLevel = (score: number) => {
  if (score >= 20) return {
    level: "Ethics Leader",
    color: "teal",
    description: "Your organization demonstrates strong AI ethics maturity. Continue refining your approach and consider sharing your expertise with others.",
    recommendation: "Advanced workshops on emerging AI ethics challenges and thought leadership opportunities."
  };
  if (score >= 13) return {
    level: "Developing Framework",
    color: "blue",
    description: "You're on the right path with foundational practices in place. Strategic guidance can help you build a comprehensive ethics framework.",
    recommendation: "Customized consulting to strengthen your AI governance and fill critical gaps."
  };
  if (score >= 7) return {
    level: "Early Stage",
    color: "yellow",
    description: "Your organization is beginning to recognize AI ethics importance. Now is the critical time to establish robust frameworks before challenges escalate.",
    recommendation: "Executive education and framework development to build organizational capacity."
  };
  return {
    level: "Foundation Needed",
    color: "red",
    description: "Significant gaps exist in your AI ethics approach. Immediate action is needed to mitigate risks and build stakeholder trust.",
    recommendation: "Comprehensive assessment and strategic roadmap development with expert guidance."
  };
};

export default function QuizSection() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [showEmailCapture, setShowEmailCapture] = useState(false);

  const handleAnswer = (score: number) => {
    setSelectedOption(score);
    setTimeout(() => {
      const newAnswers = [...answers, score];
      setAnswers(newAnswers);
      
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedOption(null);
      } else {
        setShowEmailCapture(true);
      }
    }, 300);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      alert('Please enter your email address');
      return;
    }

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('score', totalScore.toString());
      formData.append('level', getScoreLevel(totalScore));

      const response = await fetch('https://readdy.ai/api/form/d4eo1maokpliv29boang', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setShowResults(true);
      } else {
        alert('There was an error submitting your email. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting your email. Please try again.');
    }
  };

  const totalScore = answers.reduce((sum, score) => sum + score, 0);
  const maxScore = questions.length * 4;
  const scoreData = getScoreLevel(totalScore);
  const percentage = Math.round((totalScore / maxScore) * 100);

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResults(false);
    setSelectedOption(null);
    setEmail('');
    setName('');
    setShowEmailCapture(false);
  };

  const colorClasses = {
    teal: 'bg-teal-500',
    blue: 'bg-blue-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500'
  };

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {!showResults && !showEmailCapture && (
          <>
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold border-l-4 border-teal-500 pl-4 mb-6 inline-block text-left">
                AI Ethics Readiness Assessment
              </h2>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                Discover where your organization stands on the AI ethics maturity spectrum. This 2-minute assessment reveals critical gaps and opportunities.
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">
                  Question {currentQuestion + 1} of {questions.length}
                </span>
                <span className="text-sm font-medium text-teal-600">
                  {Math.round(((currentQuestion) / questions.length) * 100)}% Complete
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-teal-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentQuestion) / questions.length) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Question Card */}
            <div className="bg-gray-50 rounded-xl p-8 shadow-lg border-t-4 border-teal-500">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">
                {questions[currentQuestion].question}
              </h3>

              <div className="space-y-4">
                {questions[currentQuestion].options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(option.score)}
                    className={`w-full text-left p-5 rounded-lg border-2 transition-all cursor-pointer ${
                      selectedOption === option.score
                        ? 'border-teal-500 bg-teal-50 shadow-md'
                        : 'border-gray-200 hover:border-teal-300 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-4 flex-shrink-0 mt-0.5 ${
                        selectedOption === option.score
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedOption === option.score && (
                          <i className="ri-check-line text-white text-sm"></i>
                        )}
                      </div>
                      <span className="text-gray-800 font-medium">{option.text}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Email Capture */}
        {showEmailCapture && (
          <div className="bg-gray-50 rounded-xl p-8 shadow-lg border-t-4 border-teal-500 text-center">
            <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-mail-line text-white text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Get Your Personalized Results
            </h3>
            <p className="text-gray-700 mb-8 max-w-md mx-auto">
              Receive your AI Ethics Readiness Score along with customized recommendations for your organization.
            </p>

            <form onSubmit={handleEmailSubmit} className="space-y-4" data-readdy-form>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter your email to see your personalized results
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="your.email@company.com"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                View My Results
              </button>
            </form>
          </div>
        )}

        {/* Results */}
        {showResults && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Your AI Ethics Readiness Score
              </h2>
              <p className="text-lg text-gray-600">
                Based on your responses, here's where your organization stands
              </p>
            </div>

            {/* Score Display */}
            <div className="bg-gray-50 rounded-xl p-8 shadow-lg border-t-4 border-teal-500">
              <div className="text-center mb-8">
                <div className="inline-block relative">
                  <svg className="w-48 h-48 transform -rotate-90">
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="#e5e7eb"
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="#14b8a6"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 88}`}
                      strokeDashoffset={`${2 * Math.PI * 88 * (1 - percentage / 100)}`}
                      strokeLinecap="round"
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div>
                      <div className="text-5xl font-bold text-gray-900">{totalScore}</div>
                      <div className="text-sm text-gray-600">out of {maxScore}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center mb-6">
                <span className={`inline-block ${colorClasses[scoreData.color as keyof typeof colorClasses]} text-white px-6 py-2 rounded-full text-xl font-bold mb-4`}>
                  {scoreData.level}
                </span>
                <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                  {scoreData.description}
                </p>
              </div>

              {/* Breakdown */}
              <div className="bg-white rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-lightbulb-line text-teal-500 mr-2"></i>
                  Recommended Next Steps
                </h4>
                <p className="text-gray-700">
                  {scoreData.recommendation}
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="grid md:grid-cols-2 gap-4">
                <a
                  href="#contact"
                  className="bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-center whitespace-nowrap cursor-pointer block"
                >
                  Schedule a Consultation
                </a>
                <a
                  href="#speaking"
                  className="bg-gray-700 hover:bg-gray-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-center whitespace-nowrap cursor-pointer block"
                >
                  Book a Speaking Engagement
                </a>
              </div>

              <div className="text-center mt-6">
                <button
                  onClick={resetQuiz}
                  className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  Retake Assessment
                </button>
              </div>
            </div>

            {/* Social Sharing */}
            <div className="bg-white rounded-lg p-6 text-center border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-4">
                Share Your Results
              </h4>
              <div className="flex justify-center space-x-4">
                <button className="w-10 h-10 bg-gray-700 hover:bg-teal-600 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-white"></i>
                </button>
                <button className="w-10 h-10 bg-gray-700 hover:bg-teal-600 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-white"></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
